// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - API client for Model Management
// 2. Separation of Concerns - API calls separated from UI logic
// 3. Modular Architecture - Independent service module
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Model Management — Ollama admin (list, pull, set active, delete).
 * Active tag is stored in the DB; optional pull via API when a tag is missing.
 */

import apiClient, { API_ENDPOINTS } from '../config/api';

const BASE_URL = API_ENDPOINTS.MODEL_FINETUNE;

const modelManagementService = {
  /**
   * List installed Ollama tags and resolved active model.
   */
  getActiveModels: async () => {
    const response = await apiClient.get(`${BASE_URL}/models/active`);
    return response.data;
  },

  /**
   * Set system default Ollama tag. If pullIfMissing, runs Ollama pull when the tag is absent (long request).
   */
  setActiveModel: async (modelName, pullIfMissing = false) => {
    const encoded = encodeURIComponent(modelName);
    const response = await apiClient.put(
      `${BASE_URL}/models/${encoded}/set-active`,
      null,
      {
        params: { pull_if_missing: pullIfMissing },
        timeout: pullIfMissing ? 7_200_000 : undefined,
      },
    );
    return response.data;
  },

  /**
   * Persist voice Stage 1 Ollama tag (process worker: cleanup + EN JSON). Query stage must be 1.
   */
  setVoiceStageModel: async (modelName, stage, pullIfMissing = false) => {
    const encoded = encodeURIComponent(modelName);
    const response = await apiClient.put(
      `${BASE_URL}/models/${encoded}/set-voice-stage`,
      null,
      {
        params: { stage, pull_if_missing: pullIfMissing },
        timeout: pullIfMissing ? 7_200_000 : undefined,
      },
    );
    return response.data;
  },

  setVoiceLlmProviders: async ({ voice_process_llm_provider, voice_analysis_qa_llm_provider }) => {
    const response = await apiClient.put(`${BASE_URL}/voice-llm-providers`, {
      voice_process_llm_provider,
      voice_analysis_qa_llm_provider,
    });
    return response.data;
  },

  /** OpenAI organization costs for current UTC month (requires OPENAI_ADMIN_API_KEY on server). */
  getOpenAiOrganizationCostsMonth: async () => {
    const response = await apiClient.get(API_ENDPOINTS.MODEL_FINETUNE_OPENAI_COSTS_MONTH);
    return response.data;
  },

  /**
   * Pull an Ollama tag onto the server (may take minutes).
   */
  pullModel: async (modelName) => {
    const response = await apiClient.post(
      `${BASE_URL}/models/pull`,
      { model_name: modelName.trim() },
      { timeout: 7_200_000 },
    );
    return response.data;
  },

  /**
   * Remove model from Ollama (ollama rm).
   */
  deleteModel: async (modelName) => {
    const encoded = encodeURIComponent(modelName);
    const response = await apiClient.delete(`${BASE_URL}/models/${encoded}`);
    return response.data;
  },
};

export default modelManagementService;
